import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Menu as MenuIcon,
  Link as LinkIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';

import { useAuth } from '../../../../contexts/AuthContext';
import { 
  getNavigation, 
  saveNavigation, 
  CMSNavigation 
} from '../../../../firebase/services/cmsService';

const NavigationPage: React.FC = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [navigation, setNavigation] = useState<CMSNavigation[]>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<CMSNavigation> | null>(null);
  const [isNewItem, setIsNewItem] = useState(false);

  useEffect(() => {
    loadNavigation();
  }, []);

  const loadNavigation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const navItems = await getNavigation();
      setNavigation(navItems);
    } catch (err: any) {
      console.error('Error loading navigation:', err);
      setError(err.message || 'Failed to load navigation');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Remove IDs for saving (they'll be regenerated)
      const navToSave = navigation.map(({ id, ...item }) => item);
      await saveNavigation(navToSave);
      
      setSuccess('Navigation saved successfully!');
      // Reload to get new IDs
      await loadNavigation();
    } catch (err: any) {
      console.error('Error saving navigation:', err);
      setError(err.message || 'Failed to save navigation');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    setEditingItem({
      label: '',
      url: '',
      order: navigation.length + 1,
      isExternal: false,
      isVisible: true,
    });
    setIsNewItem(true);
    setEditDialogOpen(true);
  };

  const handleEditItem = (item: CMSNavigation) => {
    setEditingItem({ ...item });
    setIsNewItem(false);
    setEditDialogOpen(true);
  };

  const handleDeleteItem = (itemId: string) => {
    setNavigation(prev => prev.filter(item => item.id !== itemId));
  };

  const handleToggleVisibility = (itemId: string) => {
    setNavigation(prev => prev.map(item => 
      item.id === itemId 
        ? { ...item, isVisible: !item.isVisible }
        : item
    ));
  };

  const handleMoveItem = (itemId: string, direction: 'up' | 'down') => {
    const currentIndex = navigation.findIndex(item => item.id === itemId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= navigation.length) return;

    const newNavigation = [...navigation];
    [newNavigation[currentIndex], newNavigation[newIndex]] = [newNavigation[newIndex], newNavigation[currentIndex]];
    
    // Update order values
    newNavigation.forEach((item, index) => {
      item.order = index + 1;
    });

    setNavigation(newNavigation);
  };

  const handleSaveItem = () => {
    if (!editingItem) return;

    if (isNewItem) {
      const newItem: CMSNavigation = {
        ...editingItem,
        id: `nav_${Date.now()}`,
      } as CMSNavigation;
      
      setNavigation(prev => [...prev, newItem]);
    } else {
      setNavigation(prev => prev.map(item => 
        item.id === editingItem.id 
          ? { ...editingItem } as CMSNavigation
          : item
      ));
    }

    setEditDialogOpen(false);
    setEditingItem(null);
    setIsNewItem(false);
  };

  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setIsNewItem(false);
  };

  // Default navigation items if none exist
  const getDefaultNavigation = (): Omit<CMSNavigation, 'id'>[] => [
    { label: 'Home', url: '/', order: 1, isExternal: false, isVisible: true },
    { label: 'About', url: '/about', order: 2, isExternal: false, isVisible: true },
    { label: 'Marina', url: '/marina', order: 3, isExternal: false, isVisible: true },
    { label: 'Cafe', url: '/cafe', order: 4, isExternal: false, isVisible: true },
    { label: 'Store', url: '/store', order: 5, isExternal: false, isVisible: true },
    { label: 'Contact', url: '/contact', order: 6, isExternal: false, isVisible: true },
  ];

  const handleLoadDefaults = async () => {
    try {
      const defaultNav = getDefaultNavigation();
      await saveNavigation(defaultNav);
      setSuccess('Default navigation loaded successfully!');
      await loadNavigation();
    } catch (err: any) {
      console.error('Error loading default navigation:', err);
      setError(err.message || 'Failed to load default navigation');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              <MenuIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Navigation Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your website's main navigation menu
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              onClick={handleAddItem}
              startIcon={<AddIcon />}
              sx={{ mr: 2 }}
            >
              Add Item
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Save Navigation
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Navigation Items */}
        <Card>
          <CardHeader
            title="Navigation Items"
            action={
              navigation.length === 0 ? (
                <Button variant="outlined" onClick={handleLoadDefaults}>
                  Load Default Navigation
                </Button>
              ) : null
            }
          />
          <CardContent>
            {navigation.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <MenuIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography color="textSecondary" gutterBottom>
                  No navigation items created yet
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Create navigation items to build your website menu
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleAddItem}
                  startIcon={<AddIcon />}
                  sx={{ mr: 2 }}
                >
                  Add First Item
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleLoadDefaults}
                >
                  Load Default Navigation
                </Button>
              </Box>
            ) : (
              <List>
                {navigation
                  .sort((a, b) => a.order - b.order)
                  .map((item, index) => (
                    <React.Fragment key={item.id}>
                      <ListItem>
                        <ListItemIcon>
                          <DragIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1">
                                {item.label}
                              </Typography>
                              {!item.isVisible && (
                                <VisibilityOffIcon fontSize="small" color="disabled" />
                              )}
                              {item.isExternal && (
                                <LinkIcon fontSize="small" color="primary" />
                              )}
                            </Box>
                          }
                          secondary={item.url}
                        />
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveItem(item.id, 'up')}
                              disabled={index === 0}
                            >
                              <ArrowUpIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleMoveItem(item.id, 'down')}
                              disabled={index === navigation.length - 1}
                            >
                              <ArrowDownIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleToggleVisibility(item.id)}
                              color={item.isVisible ? 'primary' : 'default'}
                            >
                              {item.isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleEditItem(item)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteItem(item.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                      {index < navigation.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
              </List>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={handleCancelEdit}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {isNewItem ? 'Add Navigation Item' : 'Edit Navigation Item'}
          </DialogTitle>
          <DialogContent>
            {editingItem && (
              <Box sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Label"
                  value={editingItem.label || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                  sx={{ mb: 3 }}
                />

                <TextField
                  fullWidth
                  label="URL"
                  value={editingItem.url || ''}
                  onChange={(e) => setEditingItem({ ...editingItem, url: e.target.value })}
                  helperText="Use relative URLs for internal pages (e.g., /about) or full URLs for external links"
                  sx={{ mb: 3 }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editingItem.isVisible || false}
                          onChange={(e) => setEditingItem({ ...editingItem, isVisible: e.target.checked })}
                        />
                      }
                      label="Visible"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={editingItem.isExternal || false}
                          onChange={(e) => setEditingItem({ ...editingItem, isExternal: e.target.checked })}
                        />
                      }
                      label="External Link"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelEdit}>Cancel</Button>
            <Button onClick={handleSaveItem} variant="contained">
              {isNewItem ? 'Add Item' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default NavigationPage; 